import React from 'react';
import { Accordion, AccordionSummary, AccordionDetails, TableContainer, Table, TableRow, TableCell, TableBody, Paper } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { CreateProjectDTO } from '../project.type'; // Adjust the import path as needed
import { Typography } from 'components/index';

interface ReviewAndSubmitProps {
  projectData: CreateProjectDTO;
  onSubmit: () => void;
}

interface DataProps {
  [key: string]: any; // Replace 'any' with more specific type if possible
}

interface DataTableProps {
  data: DataProps;
  excludeFields?: string[];
}
const formatKey = (key: any) => {
  // Special mapping for field names that need specific display names
  const fieldNameMapping: { [key: string]: string } = {
    originalCompletionDateEstimated: 'Original Completion Date',
    revisedCompletionDate: 'Revised Completion Date',
    bankLoanExpirationDate: 'Bank Loan Expiration Date',
  };

  // If there's a specific mapping, use it
  if (fieldNameMapping[key]) {
    return fieldNameMapping[key];
  }

  // Capitalize the first letter
  let formattedKey = key.charAt(0).toUpperCase() + key.slice(1);

  // Add space before a capital letter that follows a lowercase letter
  formattedKey = formattedKey.replace(/([a-z])([A-Z])/g, '$1 $2');

  // Handle consecutive capital letters
  formattedKey = formattedKey.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');

  return formattedKey;
};

const DataTable: React.FC<DataTableProps> = ({ data, excludeFields = [] }) => {
  const formatValue = (value: any) => {
    if (value && typeof value === 'object') {
      // Handle UserDTO object (observer)
      if (value.firstName && value.lastName) {
        return `${value.firstName} ${value.lastName}`;
      }
      // Handle CorporationDTO object
      if (value.name) {
        return value.name;
      }
      // Handle address object
      if (value.street || value.city || value.state || value.postalCode) {
        const { street, city, state, postalCode } = value;
        return [street, city, state, postalCode].filter(Boolean).join(', ');
      }
    }
    return value !== null && value !== undefined && value !== '' ? value.toString() : '';
  };

  return (
    <TableContainer component={Paper} style={{ marginBottom: '0px', borderRadius: '0px 0px 10px 10px' }}>
      <Table size='small' aria-label='a dense table' sx={{ margin: '0px' }}>
        <TableBody>
          {Object.entries(data).map(([key, value]) => {
            if (excludeFields.includes(key)) {
              return null; // Skip rendering this row
            }
            return (
              <TableRow key={key} sx={{ '& td, & th': { border: 0 } }}>
                <TableCell scope='row'>
                  <Typography variant='subtitle:h6'>{formatKey(key)}</Typography>
                </TableCell>
                <TableCell align='right'>
                  <Typography variant='subtitle:h6'>{formatValue(value)}</Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const ReviewAndSubmit: React.FC<ReviewAndSubmitProps> = ({ projectData, onSubmit }) => {
  return (
    <div style={{ marginTop: '10px' }}>
      <Typography variant='h6' style={{ marginBottom: '20px' }}>
        Review Input Data
      </Typography>

      {/* Accordion for Project Info */}
      <Accordion
        elevation={1}
        style={{ marginTop: '5px', borderRadius: '8px' }}
        sx={{
          '&:before': {
            display: 'none',
          },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ my: '5px' }} variant='h6'>
            Project Information
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          <DataTable data={projectData.projectInfo} />
        </AccordionDetails>
      </Accordion>

      {/* Accordion for Client and Borrower Info */}
      <Accordion
        elevation={1}
        style={{ marginTop: '5px', borderRadius: '8px' }}
        sx={{
          '&:before': {
            display: 'none',
          },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ my: '5px' }} variant='h6'>
            Client Information
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          <DataTable data={projectData.client} excludeFields={['company']} />
        </AccordionDetails>
      </Accordion>
      {/* Accordion for Client and Borrower Info */}
      <Accordion
        elevation={1}
        style={{ marginTop: '5px', borderRadius: '8px' }}
        sx={{
          '&:before': {
            display: 'none',
          },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ my: '5px' }} variant='h6'>
            Borrower Information
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          <DataTable data={projectData.borrower} excludeFields={['loanOfficerName']} />
        </AccordionDetails>
      </Accordion>

      {/* Accordion for Other Info */}
      <Accordion
        elevation={1}
        style={{ marginTop: '5px', borderRadius: '8px' }}
        sx={{
          '&:before': {
            display: 'none',
          },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ my: '5px' }} variant='h6'>
            Other Information
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: 0 }}>
          <DataTable data={projectData.otherInfo} excludeFields={['loanOfficerName']} />
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default ReviewAndSubmit;
